import React from 'react';
import ReactECharts from 'echarts-for-react';
import useBodyLanguageData from './BodyLanguageData';

const palette = [
  '#0080ff',
  '#00ccff',
  '#00ffff',
  '#ffa500',
  '#ff8c00',
  '#ffd700',
  '#cc00cc',
  '#ff00ff',
  '#fe75fe',
  '#a64dff',
  '#4dd2ff',
  '#66ffcc'
];

const BodyLanguageDistributionChart = ({ artworkId, embedded = false }) => {
  const { processedData, loading, error } = useBodyLanguageData(artworkId);
  const distribution = processedData?.distribution || [];
  const shellClass = embedded
    ? 'w-full h-full flex items-center justify-center text-center text-white'
    : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-full text-center text-white';

  if (loading) {
    return <div className={shellClass}>Loading body language data...</div>;
  }

  if (error) {
    return (
      <div className={`${shellClass} text-red-400`}>
        {error}
      </div>
    );
  }

  if (!distribution.length) {
    return (
      <div className={`${shellClass} text-gray-300`}>
        No body language data available for this artwork
      </div>
    );
  }

  // 3-color gradients for each body language type
  const getBodyLanguageGradient = (index) => {
    const baseColor = palette[index % palette.length];
    // Convert hex to RGB for gradient creation
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(baseColor);
    if (!rgb) return baseColor;
    
    // Create lighter and darker variations
    const lighten = (r, g, b, factor) => ({
      r: Math.min(255, Math.round(r + (255 - r) * factor)),
      g: Math.min(255, Math.round(g + (255 - g) * factor)),
      b: Math.min(255, Math.round(b + (255 - b) * factor))
    });
    
    const darken = (r, g, b, factor) => ({
      r: Math.max(0, Math.round(r * (1 - factor))),
      g: Math.max(0, Math.round(g * (1 - factor))),
      b: Math.max(0, Math.round(b * (1 - factor)))
    });
    
    const light = lighten(rgb.r, rgb.g, rgb.b, 0.3);
    const dark = darken(rgb.r, rgb.g, rgb.b, 0.2);
    
    const toHex = (r, g, b) => `#${[r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
    
    return {
      type: 'linear',
      x: 0, y: 0, x2: 1, y2: 1,
      colorStops: [
        { offset: 0, color: toHex(dark.r, dark.g, dark.b) },
        { offset: 0.5, color: baseColor },
        { offset: 1, color: toHex(light.r, light.g, light.b) }
      ]
    };
  };

  const getBodyLanguageGlowColor = (index) => {
    const baseColor = palette[index % palette.length];
    // Convert hex to rgba
    const hexToRgba = (hex, alpha) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})` : `rgba(158, 158, 158, ${alpha})`;
    };
    return hexToRgba(baseColor, 0.7);
  };

  const option = {
    title: {
      text: 'Body Language Distribution',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'item',
      appendToBody: embedded,
      formatter: '{b}: {c} ({d}%)',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15]
    },
    legend: {
      orient: 'horizontal',
      bottom: '15%',
      left: 'center',
      itemGap: 10,
      itemWidth: 14,
      itemHeight: 12,
      itemBorderWidth: 1,
      itemBorderColor: '#e5e5e5',
      // padding: [4, 8],
      textStyle: {
        fontSize: 12,
        fontWeight: 'normal',
        color: '#e0e0e0'
      }
    },
    series: [
      {
        name: 'Body Language',
        type: 'pie',
        radius: ['30%', '50%'],
        center: ['50%', '35%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#EEE',
          borderWidth: 1,
          shadowBlur: 20,
          shadowOffsetX: 0,
          shadowOffsetY: 0
        },
        label: {
          show: true,
          formatter: '{b}\n({c})',
          fontSize: 13,
          fontWeight: 'bold',
          color: '#ffffff'
        },
        labelLine: {
          length: 20,
          length2: 15,
          lineStyle: {
            width: 2
          }
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          },
          itemStyle: {
            shadowBlur: 35,
            shadowColor: 'rgba(255, 255, 255, 0.9)',
            borderWidth: 0
          },
          scale: true,
          scaleSize: 4
        },
        data: distribution.map((item, index) => ({
          value: item.count,
          name: item.label,
          itemStyle: {
            color: getBodyLanguageGradient(index),
            shadowBlur: 18,
            shadowColor: getBodyLanguageGlowColor(index),
            shadowOffsetX: 0,
            shadowOffsetY: 0
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 35,
              shadowColor: getBodyLanguageGlowColor(index).replace('0.7', '0.9')
            }
          }
        }))
      }
    ],
    animationType: 'scale',
    animationEasing: 'elasticOut',
    animationDelay: function (idx) {
      return Math.random() * 200;
    }
  };

  return (
    <div className={embedded ? 'w-full h-full' : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800'}>
      <div className={embedded ? 'h-full' : 'h-80'}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default BodyLanguageDistributionChart;


